import { useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { useNotifications } from "@/context/NotificationContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";

export default function AdminSettings() {
  const { showToast } = useNotifications();
  const { user, updateUser } = useAuth();
  const [settings, setSettings] = useState({
    companyName: user?.company || "Schemackozo",
    email: user?.email || "info@schemackozo.com",
    supportEmail: "support@schemackozo.com",
    timezone: "UTC",
    currency: "USD",
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update user profile with company and email
      const updateData: any = {};
      // Backend /api/users/profile updates only `company` and `email` columns in `users`.
      // Avoid sending `undefined` (can cause unexpected parsing issues server-side).
      if (settings.companyName && settings.companyName !== user?.company) updateData.company = settings.companyName;
      if (settings.email && settings.email !== user?.email) updateData.email = settings.email;

      console.log("Saving settings:", updateData);

      if (Object.keys(updateData).length > 0) {
        const res = await api.patch("/users/profile", updateData);
        console.log("Backend response:", res.data);
        const updatedUser = res.data.data;
        updateUser({
          name: updatedUser.full_name,
          email: updatedUser.email,
          company: updatedUser.company,
        });
      } else {
        console.log("No changes to save");
      }

      // TODO: Save other settings (supportEmail, timezone, currency) to backend when supported

      showToast("success", "Settings saved successfully.");
    } catch (e: any) {
      console.error("Failed to save settings:", e);
      showToast("error", e.response?.data?.message || "Failed to save settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1
            className="text-2xl font-black text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Settings
          </h1>
          <p className="text-silver text-sm mt-1">Configure platform-wide settings.</p>
        </div>

        {/* Company */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
          <h2
            className="text-white font-bold text-base mb-5"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Company Information
          </h2>
          <div className="space-y-4">
            {[
              { label: "Company Name", key: "companyName" as const, type: "text" },
              { label: "Primary Email", key: "email" as const, type: "email" },
              { label: "Support Email", key: "supportEmail" as const, type: "email" },
            ].map((f) => (
              <div key={f.key}>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">
                  {f.label}
                </label>
                <input
                  type={f.type}
                  value={settings[f.key]}
                  onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Timezone", key: "timezone" as const, options: ["UTC", "EST", "PST", "WAT", "EAT"] },
                { label: "Currency", key: "currency" as const, options: ["USD", "EUR", "GBP", "NGN", "KES"] },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">
                    {f.label}
                  </label>
                  <select
                    value={settings[f.key]}
                    onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                    className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 cursor-pointer"
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={loading}
          className="bg-electric-blue text-deep-black font-bold px-8 py-3 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {loading ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </AdminLayout>
  );
}
